import type { TFunction } from 'i18next'

export const LOCATION_TYPES = {
  COMMON_AREA: 'common-area',
  OTHER: 'other',
  UNIT: 'unit',
  UNIT_TYPES: 'unit-types',
} as const

export type LocationType = (typeof LOCATION_TYPES)[keyof typeof LOCATION_TYPES]

export type LocationValue = string | string[] | null | undefined

export type UnitOption = { label: string; value: string }

// Note: react-select has no native select-all, so "All units" is a synthetic option that
// expands to every fetched unit as soon as it is picked. The sentinel value stays a fixed,
// untranslated string - only the visible label goes through i18n.
export const ALL_UNITS_VALUE = '__all-units__'

export const getAllUnitsOption = (t: TFunction<['internal-inspections']>): UnitOption => ({
  label: t('internal-inspections:locationInput.allUnits'),
  value: ALL_UNITS_VALUE,
})

export const toUnitArray = (locationValue: LocationValue): string[] => {
  if (Array.isArray(locationValue)) return locationValue
  return locationValue ? [locationValue] : []
}

// Note: a unit is stored and rendered exactly as the API returns it - the picker used to add a
// "#" display prefix to every option, which then got persisted as the locationValue. Purely
// numeric units can come back as numbers, so they are stringified to keep locationValue a
// string for every location type, as the BE expects.
export const toUnitOption = (unit: string): UnitOption => ({
  label: String(unit),
  value: String(unit),
})

// Note: the BE accepts both shapes for locationType "unit"; a single unit is sent as a
// plain string so pre-multi-select setups and their consumers keep the legacy contract.
export const buildLocationValuePayload = (
  locationType: LocationType,
  locationValue: LocationValue,
): LocationValue => {
  if (locationType === LOCATION_TYPES.UNIT && Array.isArray(locationValue)) {
    return locationValue.length === 1 ? locationValue[0] : locationValue
  }
  return locationValue
}

export const getMultiUnitCautionText = (
  t: TFunction<['internal-inspections']>,
  inspectionName: string,
  unitCount: number,
): string =>
  t('internal-inspections:scheduleInspectionView.multiUnitCautionText', {
    count: unitCount,
    name: inspectionName,
  })

export const getUnitSelectOptions = (
  t: TFunction<['internal-inspections']>,
  unitOptions: readonly UnitOption[],
  selectedUnits: readonly string[],
): UnitOption[] =>
  selectedUnits.length === unitOptions.length
    ? [...unitOptions]
    : [getAllUnitsOption(t), ...unitOptions]

export const resolveUnitSelection = (
  items: readonly UnitOption[] | null,
  unitOptions: readonly UnitOption[],
): string[] => {
  const selected = items || []
  if (selected.some(({ value }) => value === ALL_UNITS_VALUE)) {
    return unitOptions.map(({ value }) => value)
  }
  return selected.map(({ value }) => value)
}

export const isLocationValueFilled = (value: unknown): boolean => {
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'string') return value.trim().length > 0
  return !!value
}
